import type { FC, ReactNode } from 'react'
import type { Profile, SocialLink } from '@/types'

import Link from 'next/link'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { NAV_LINKS, siteConfig } from '@/constants/site'

interface SiteFooterProps {
    profile: Profile | null
    socialLinks: Array<SocialLink>
}

const linkClassName = 'rounded-sm text-foreground/80 transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50'

export const SiteFooter: FC<SiteFooterProps> = ({ profile, socialLinks }): ReactNode => {
    const scrollToTop = () => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' })
    }

    return (
        <footer id='site-footer' className='border-t border-border'>
            <Container className='py-14 sm:py-20'>
                <div className='grid gap-12 md:grid-cols-12'>
                    <div className='md:col-span-6'>
                        {profile
                            ? (
                                <p
                                    data-mdlfy-cms={`profile:${profile.id}:name`}
                                    className='font-heading text-4xl leading-none sm:text-5xl'
                                >
                                    {profile.name}
                                </p>
                            )
                            : (
                                <p className='font-heading text-4xl leading-none sm:text-5xl'>
                                    {siteConfig.name}
                                </p>
                            )}

                        {profile?.role && (
                            <p
                                data-mdlfy-cms={`profile:${profile.id}:role`}
                                className='mt-4 text-muted-foreground'
                            >
                                {profile.role}
                            </p>
                        )}

                        {profile?.location && (
                            <p
                                data-mdlfy-cms={`profile:${profile.id}:location`}
                                className='text-muted-foreground'
                            >
                                {profile.location}
                            </p>
                        )}
                    </div>

                    <nav aria-label='Footer' className='md:col-span-3'>
                        <Eyebrow as='h2'>
                            Pages
                        </Eyebrow>

                        <ul className='mt-4 space-y-2.5'>
                            <li>
                                <Link href='/' className={linkClassName}>
                                    Home
                                </Link>
                            </li>

                            {NAV_LINKS.map(link => (
                                <li key={link.href}>
                                    <Link href={link.href} className={linkClassName}>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='md:col-span-3'>
                        <Eyebrow as='h2'>
                            Connect
                        </Eyebrow>

                        <ul className='mt-4 space-y-2.5'>
                            {profile?.email && (
                                <li>
                                    <a href={`mailto:${profile.email}`} className={linkClassName}>
                                        <span data-mdlfy-cms={`profile:${profile.id}:email`}>{profile.email}</span>
                                    </a>
                                </li>
                            )}

                            {socialLinks.map(link => {
                                const external = link.url.startsWith('http')

                                return (
                                    <li key={link.id}>
                                        <a
                                            href={link.url}
                                            target={external ? '_blank' : undefined}
                                            rel={external ? 'noopener noreferrer' : undefined}
                                            className={`group inline-flex items-center gap-1 ${linkClassName}`}
                                        >
                                            <span data-mdlfy-cms={`social_links:${link.id}:label`}>{link.label}</span>

                                            <ArrowUpRight className='size-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                        </a>
                                    </li>
                                )
                            })}

                            <li>
                                <Link href='/contact' className={linkClassName}>
                                    Send a message
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className='mt-14 flex flex-col-reverse gap-4 border-t border-border pt-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        <span suppressHydrationWarning>&copy; {new Date().getFullYear()}</span>
                        {' '}
                        {profile
                            ? <span data-mdlfy-cms={`profile:${profile.id}:name`}>{profile.name}</span>
                            : <span>{siteConfig.name}</span>}
                    </p>

                    <button
                        type='button'
                        onClick={scrollToTop}
                        className='inline-flex items-center gap-1.5 self-start rounded-sm transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 sm:self-auto'
                    >
                        Back to top

                        <ArrowUp className='size-4' />
                    </button>
                </div>
            </Container>
        </footer>
    )
}
